import { useEffect } from 'react'
import { syncDynamicYieldConsent } from 'src/utils/dynamicYieldConsent'

import {
  DYNAMIC_YIELD_API_DYNAMIC_SCRIPT_URL,
  DYNAMIC_YIELD_API_STATIC_SCRIPT_URL,
} from './DynamicYieldHead'
import { DYNAMIC_YIELD_CONTEXT_READY_EVENT } from './DynamicYieldPageContext'

export const COOKIEBOT_READY = 'CookiebotOnConsentReady'
export const COOKIEBOT_ACCEPT = 'CookiebotOnAccept'
export const COOKIEBOT_DECLINE = 'CookiebotOnDecline'

export const DYNAMIC_YIELD_SCRIPT_READY_EVENT = 'dynamic-yield:script-ready'

const dynamicYieldScriptUrls = {
  api_dynamic: DYNAMIC_YIELD_API_DYNAMIC_SCRIPT_URL,
  api_static: DYNAMIC_YIELD_API_STATIC_SCRIPT_URL,
} as const

const appendDynamicYieldScript = (
  scriptName: keyof typeof dynamicYieldScriptUrls
) => {
  // Gatsby puo' ri-renderizzare i wrapper durante la navigazione. Il controllo
  // tramite data attribute evita di caricare lo stesso script DY piu' volte.
  const scriptUrl = dynamicYieldScriptUrls[scriptName]

  if (
    document.querySelector(
      `script[data-dynamic-yield-script="${scriptName}"], script[src="${scriptUrl}"]`
    )
  ) {
    return
  }

  const script = document.createElement('script')

  script.type = 'text/javascript'
  script.async = true
  script.dataset.dynamicYieldScript = scriptName
  script.src = scriptUrl
  script.onload = () => {
    syncDynamicYieldConsent()
    window.dispatchEvent(new CustomEvent(DYNAMIC_YIELD_SCRIPT_READY_EVENT))
  }

  document.head.appendChild(script)
}

const loadDynamicYieldScripts = () => {
  // Queste chiamate scaricano davvero DY. Una volta caricati, gli script
  // leggono window.DY.recommendationContext e iniettano il DOM campagna nei
  // placement corrispondenti.
  appendDynamicYieldScript('api_dynamic')
  appendDynamicYieldScript('api_static')
}

function DynamicYieldScriptLoader() {
  // Componente globale montato da gatsby-browser. Non renderizza UI: il suo
  // unico scopo e' caricare gli script DY nel browser dopo hydration.
  useEffect(() => {
    // I preconnect DY vengono gia' emessi durante SSR da DynamicYieldPreconnect.
    // Qui carichiamo solo gli script runtime dopo hydration.
    // CookieBot viene caricato da GTM: puo' arrivare dopo DY oppure dopo una
    // scelta utente. Ogni evento riallinea il flag Active Consent letto da DY.
    syncDynamicYieldConsent()

    window.addEventListener(COOKIEBOT_READY, syncDynamicYieldConsent)
    window.addEventListener(COOKIEBOT_ACCEPT, syncDynamicYieldConsent)
    window.addEventListener(COOKIEBOT_DECLINE, syncDynamicYieldConsent)

    if (window.DY?.recommendationContext) {
      loadDynamicYieldScripts()

      return () => {
        window.removeEventListener(COOKIEBOT_READY, syncDynamicYieldConsent)
        window.removeEventListener(COOKIEBOT_ACCEPT, syncDynamicYieldConsent)
        window.removeEventListener(COOKIEBOT_DECLINE, syncDynamicYieldConsent)
      }
    }

    window.addEventListener(
      DYNAMIC_YIELD_CONTEXT_READY_EVENT,
      loadDynamicYieldScripts,
      { once: true }
    )

    return () => {
      window.removeEventListener(
        DYNAMIC_YIELD_CONTEXT_READY_EVENT,
        loadDynamicYieldScripts
      )
      window.removeEventListener(COOKIEBOT_READY, syncDynamicYieldConsent)
      window.removeEventListener(COOKIEBOT_ACCEPT, syncDynamicYieldConsent)
      window.removeEventListener(COOKIEBOT_DECLINE, syncDynamicYieldConsent)
    }
  }, [])

  return null
}

export default DynamicYieldScriptLoader
